export type LongWallsMilestoneStatus = "validated" | "running" | "planned" | "blocked";

export type LongWallsProductVersion = "foundation" | "v1" | "v2" | "v3";

export interface LongWallsVersionDefinition {
  id: LongWallsProductVersion;
  label: string;
  strategicMeaning: string;
  closureRule: string;
}

export interface LongWallsMilestoneTest {
  name: string;
  command?: string;
  proof: string;
  scope: string;
}

export interface LongWallsStrategicMilestone {
  id: string;
  horizon: string;
  targetVersion: LongWallsProductVersion;
  title: string;
  status: LongWallsMilestoneStatus;
  headline: string;
  strategicMeaning: string;
  proofSummary: string;
  evidenceRefs: string[];
  proofHighlights: string[];
  remaining: string[];
  tests: LongWallsMilestoneTest[];
  claimBoundary: string;
}

export const longwallsVersionDefinitions: LongWallsVersionDefinition[] = [
  {
    id: "foundation",
    label: "Foundation - Strategy to engine spine",
    strategicMeaning:
      "The platform moved from scattered product ideas toward one operating model: Atlas frames the AI roadmap, Foundry turns intent into contracts, Jarvis builds, and LongWalls governs proof.",
    closureRule: "Foundation is closed only when the operating model is visible in code, docs and product surfaces.",
  },
  {
    id: "v1",
    label: "V1 - Governed bounded industrial pilot",
    strategicMeaning:
      "V1 is not unlimited app generation. It proves a supported, local-first industrial path from Foundry contract to Jarvis-generated app, QA, repair, runtime and evidence.",
    closureRule: "V1 closes only when supported cases pass the nominal path without hidden manual implementation or unsafe egress.",
  },
  {
    id: "v2",
    label: "V2 - Enterprise software factory",
    strategicMeaning:
      "V2 turns the bounded pilot into a repeatable enterprise product with commercial deployment modes, execution nodes, recipe packs, deeper Codex lanes, operational support boundaries and trust/compliance readiness.",
    closureRule:
      "V2 closes only when several enterprise pilots pass the same production-grade lane, the commercial worker model is real, and SOC2/VAPT/privacy/vendor-readiness evidence is visible without overclaiming.",
  },
  {
    id: "v3",
    label: "V3 - Jarvis-like listed-group grade autonomy",
    strategicMeaning:
      "V3 is the Tony Stark target: a governed software production partner able to run multi-day autonomous builds, multiple app families, production controls and external assurance posture at enterprise scale.",
    closureRule:
      "V3 closes only with live production controls, multi-day deep Codex proof, staffed operations, multi-app scale evidence and externally tested or audited trust claims where asserted.",
  },
];

export const longwallsMilestones: LongWallsStrategicMilestone[] = [
  {
    id: "m-vision-operating-model-2025-11",
    horizon: "Nov 2025 - Feb 2026",
    targetVersion: "foundation",
    title: "The LongWalls operating model becomes explicit",
    status: "validated",
    headline:
      "The project stopped being a generic app builder and became a three-stage software factory: Atlas, Foundry and Jarvis under LongWalls governance.",
    strategicMeaning:
      "This is the strategic root: companies do not just ask for an app. They build an AI roadmap, convert it into build-ready specifications, then let Jarvis execute under evidence and governance.",
    proofSummary:
      "The codebase and documents now carry the Atlas/Foundry/Jarvis/LongWalls separation, auth foundations, CI/gate discipline and the first roadmap-to-execution surfaces.",
    evidenceRefs: [
      "docs/ai-roadmap-implementation-plan.md",
      "docs/architecture/auth-foundation-plan.md",
      "docs/CI.md",
      "docs/longwalls-v1-v3-product-vision-critical-review.md",
    ],
    proofHighlights: [
      "Atlas is positioned as the AI roadmap and use-case intake layer.",
      "Foundry is positioned as the specification and build-readiness layer.",
      "Jarvis is positioned as the autonomous execution engine, not merely a demo generator.",
      "LongWalls is positioned as the evidence, runtime and governance control plane.",
    ],
    remaining: [
      "Keep every future lot tied to this operating model instead of app-specific excitement.",
      "Avoid using product version labels for technical script versions.",
    ],
    tests: [
      {
        name: "Strategic scope guard",
        proof: "The canonical product review defines V1, V2 and V3 as product horizons, not random milestone labels.",
        scope: "Strategy",
      },
      {
        name: "Platform governance baseline",
        proof: "CI and auth foundation docs exist as early controls for the product machine.",
        scope: "Governance",
      },
    ],
    claimBoundary:
      "This milestone proves the strategic frame. It does not prove autonomous generation depth by itself.",
  },
  {
    id: "m-first-enterprise-chain-2026-04-06",
    horizon: "Mar - 06 Apr 2026",
    targetVersion: "foundation",
    title: "The first enterprise A-to-Z chain appears",
    status: "validated",
    headline:
      "LongWalls gained an enterprise shell, Jarvis bridge, pilot chain, sponsor pack and the first A-to-Z proof path.",
    strategicMeaning:
      "This was the first visible moment where the product could start telling a full story: a business idea enters LongWalls, is governed through modules, then reaches Jarvis as an executable delivery chain.",
    proofSummary:
      "Git history records the enterprise shell, Jarvis bridge, enterprise pilot chain, repeatability and sponsor-pack gates.",
    evidenceRefs: [
      "docs/sprint-longwalls-production-runtime-verification-v1.md",
      "docs/sprint-longwalls-customer-runtime-bridge-v1.md",
      "artifacts/longwalls-platform/platform_truth_board.v1.json",
    ],
    proofHighlights: [
      "Enterprise pilot chain and Jarvis bridge were installed.",
      "Sponsor-grade pack and support/runbook thinking entered the product.",
      "Repeatability became a visible concern rather than a narrative claim.",
    ],
    remaining: [
      "Move from pilot chain narrative to one nominal, enforceable Jarvis execution path.",
      "Prevent sponsor-grade wording from outrunning executable proof.",
    ],
    tests: [
      {
        name: "Enterprise pilot chain gates",
        proof: "The early chain required explicit artifacts and gates before stronger claims could be made.",
        scope: "Pilot chain",
      },
      {
        name: "Sponsor-pack evidence",
        proof: "Sponsor, runbook and support artifacts became part of closure expectations.",
        scope: "Delivery",
      },
    ],
    claimBoundary:
      "This was a first enterprise chain, not yet a hardened industrial engine.",
  },
  {
    id: "m-brick-library-and-generalization-2026-04-10",
    horizon: "07 - 17 Apr 2026",
    targetVersion: "foundation",
    title: "The system stops pretending every app is bespoke",
    status: "validated",
    headline:
      "The Brick Library, Foundry catalog wiring, Jarvis catalog wiring, policy packs, Eclosia no-go benchmark and Conciergerie truth reset changed the operating discipline.",
    strategicMeaning:
      "The project learned that Jarvis must assemble governed service bricks and reject shallow benchmarks. This is where the engine moved away from one-off app production toward reusable capability.",
    proofSummary:
      "Brick harvest phases, Foundry/Jarvis catalog wiring, security/UX packs, repeatability proof, Eclosia no-go and Conciergerie replay preparation were produced.",
    evidenceRefs: [
      "docs/sprint-longwalls-lots-24-35.md",
      "docs/eclosia-conciergerie-foundry-codex-requirements.md",
      "artifacts/industrial-app-factory/90-percent-closure-verdict.v1.json",
    ],
    proofHighlights: [
      "Service bricks became typed and reusable.",
      "Foundry and Jarvis started consuming the same catalog instead of drifting.",
      "False benchmark optimism was challenged and capped by observable proof.",
      "Conciergerie became a deep validation reference, not the product goal.",
    ],
    remaining: [
      "Ensure bricks are selected and assembled by Jarvis inside the nominal path.",
      "Keep Conciergerie as a validation case only, not a special manual project.",
    ],
    tests: [
      {
        name: "Brick catalog gates",
        command: "pnpm -w longwalls:brick-library:v3:check",
        proof: "Typed bricks, policy binding and compatibility matrices are required before assembly claims.",
        scope: "Service bricks",
      },
      {
        name: "Benchmark truth discipline",
        command: "pnpm -w longwalls:benchmark-truth-v4:check",
        proof: "Scores are capped by source, code, runtime and QA evidence.",
        scope: "Truth scoring",
      },
    ],
    claimBoundary:
      "Brick reuse and better truth scoring do not yet prove multi-hour autonomous development.",
  },
  {
    id: "m-industrial-engine-spine-2026-04-20",
    horizon: "19 - 20 Apr 2026",
    targetVersion: "v1",
    title: "Jarvis becomes an industrial engine candidate",
    status: "validated",
    headline:
      "The industrial acceptance gates, execution plan compiler, scaffold v3, Foundry long run, restart drill and runtime evidence loop made the engine testable.",
    strategicMeaning:
      "This is the first real move toward V1: Jarvis is no longer evaluated by how nice a demo looks, but by whether it can consume a contract, plan deterministically, generate a structured app, run, repair and produce evidence.",
    proofSummary:
      "The 10-condition acceptance test, engine tunnel, no-bypass provenance gate, execution plan, scaffold v3 and runtime evidence loop became executable controls.",
    evidenceRefs: [
      "docs/industrial-app-factory-acceptance-test-v1.md",
      "docs/industrial-app-factory-engine-tunnel-v1.md",
      "artifacts/industrial-app-factory/jarvis-execution-plan.v1.json",
      "artifacts/industrial-app-factory/jarvis-scaffold-v3.v1.json",
    ],
    proofHighlights: [
      "The project switched from closing lots to passing acceptance conditions.",
      "No-bypass provenance separated Jarvis-generated capability from human/script materialization.",
      "Scaffold v3 raised the bar above compact shell apps.",
      "Runtime, QA, repair and ledger became part of the same closure story.",
    ],
    remaining: [
      "Prove the full chain repeatedly on real deep runs, not just bounded local checks.",
      "Make every go claim evidence-bound at capability level.",
    ],
    tests: [
      {
        name: "Engine tunnel",
        command: "pnpm -w longwalls:engine-tunnel:check",
        proof: "S0-S8 closure discipline is mandatory before claiming industrial progress.",
        scope: "Governance",
      },
      {
        name: "No-bypass provenance",
        command: "pnpm -w longwalls:no-bypass:check",
        proof: "Human patched and script materialized outputs cannot close Jarvis industrial capability.",
        scope: "Provenance",
      },
      {
        name: "Scaffold v3",
        command: "pnpm -w jarvis:scaffold-v3:check",
        proof: "Generated apps must include frontend, backend, data, tests, docs and operations control.",
        scope: "Jarvis",
      },
    ],
    claimBoundary:
      "This is a strong V1 engine spine, not proof that Jarvis can yet build any enterprise app autonomously.",
  },
  {
    id: "m-deep-bounded-factory-2026-04-24",
    horizon: "21 - 24 Apr 2026",
    targetVersion: "v1",
    title: "The bounded factory reaches a credible V1 claim",
    status: "validated",
    headline:
      "Writable real runs, browser repair, pressure proof, budget enforcement, enterprise pilots, Atlas native productization, security/ops controls and closure boards converged.",
    strategicMeaning:
      "This is the 'look how far we came' milestone: LongWalls can now show a bounded but coherent factory path across Atlas, Foundry, Jarvis, runtime, QA, repair and evidence.",
    proofSummary:
      "A36-A74 closed the bounded supported scope: real writable lane, enterprise breadth, generated runtime operability, Atlas native-first, policy packs, Brick Library v3, production lifecycle and honest claim caps.",
    evidenceRefs: [
      "artifacts/longwalls-platform/industrial_closure_board.v1.json",
      "artifacts/longwalls-platform/atlas_native_productization.v1.json",
      "artifacts/longwalls-platform/generated_runtime_operability.v1.json",
      "artifacts/longwalls-platform/production_lifecycle.v1.json",
    ],
    proofHighlights: [
      "Atlas moved beyond Airtable-only intake toward native forms, process design, groups and Foundry export.",
      "Foundry gained stricter contract completeness and refusal of incomplete contracts.",
      "Jarvis proved bounded writable generation, repair, QA and runtime evidence.",
      "LongWalls gained truth boards, cost guards, security/ops gates and production lifecycle boundaries.",
    ],
    remaining: [
      "The V1 claim is bounded supported scope only.",
      "Actual wall-clock multi-day proof and deeper Codex app generation remain open.",
      "Live enterprise controls such as SIEM/WAF/VAPT and staffed hypercare are not yet proven.",
    ],
    tests: [
      {
        name: "Atlas native productization",
        command: "pnpm -w longwalls:atlas-native-productization:v1:check",
        proof: "Native intake, process designer, group workflow, sponsor dossier and Foundry export pass.",
        scope: "Atlas",
      },
      {
        name: "Listed-company controls",
        command: "pnpm -w longwalls:listed-company-security-ops:v1:check",
        proof: "IAM/OIDC mapping, RBAC, audit, secrets, observability, support, incident and VAPT/WAF/SIEM readiness pass as bounded controls.",
        scope: "Security/ops",
      },
      {
        name: "Industrial closure board",
        command: "pnpm -w longwalls:industrial-closure-board:v1:check",
        proof: "The 10 conditions close only for bounded supported scope and blocked overclaims stay visible.",
        scope: "Truth board",
      },
    ],
    claimBoundary:
      "Validated V1 is bounded. It does not claim unlimited app generation, dozens-parallel production or listed-group live operations.",
  },
  {
    id: "m-nominal-path-and-runtime-cockpit-2026-04-27",
    horizon: "25 - 27 Apr 2026",
    targetVersion: "v1",
    title: "The real runtime becomes observable and governed",
    status: "running",
    headline:
      "C3-C8 installed the canonical Foundry-to-Jarvis path, A75 launched a 12-lane 48h local proof, and Runtime Cockpit made the run visible from LongWalls cloud/mobile.",
    strategicMeaning:
      "This is the current strategic inflection: we are no longer just building engine parts; we are proving whether LongWalls can supervise real autonomous runtime safely over time.",
    proofSummary:
      "The canonical `jarvis:foundry-contract:run` path exists, evidence ledger closure is stricter, A75 is running, and signed/redacted low-egress snapshots are visible through Runtime Cockpit.",
    evidenceRefs: [
      "artifacts/longwalls-platform/c4_nominal_full_local_loop.v1.json",
      "artifacts/longwalls-platform/c5_industrial_evidence_ledger.v1.json",
      "artifacts/longwalls-platform/a75-real-fleet-run-v1/live/status.json",
      "artifacts/longwalls-platform/runtime-cockpit-live-mobile-v1/production-mobile-verification-report.json",
    ],
    proofHighlights: [
      "A single nominal Jarvis command now links Foundry contract admission to execution planning and local proof.",
      "Runtime Cockpit separates Codex claimed done from LongWalls verified done.",
      "Local-to-cloud telemetry is signed, redacted, scoped and low-egress.",
      "A75 is testing 12 lanes over 48h without unsafe egress or live provider mutation.",
    ],
    remaining: [
      "A75 must finish and finalize before it becomes validated.",
      "A83-A87 must be built before any Test 2 Codex depth run.",
      "The mobile account path still needs final live-user verification before claiming fully validated mobile production access.",
    ],
    tests: [
      {
        name: "Nominal Jarvis path",
        command: "pnpm -w longwalls:c4-nominal-full-local-loop:v1:check",
        proof: "The nominal path executes local generation, QA, runtime smoke, repair/retest and evidence ledger.",
        scope: "Jarvis",
      },
      {
        name: "A75 live status",
        command: "pnpm -w longwalls:a75-real-fleet-run:v1:status",
        proof: "12 lanes, fresh heartbeats, API disabled and unsafe egress disabled must remain visible.",
        scope: "Runtime",
      },
      {
        name: "A75 finalization",
        command: "pnpm -w longwalls:a75-real-fleet-run:v1:finalize",
        proof: "48h wall-clock continuity, lane ledgers, safety controls and no unsafe egress must pass before validation.",
        scope: "Closure",
      },
      {
        name: "Runtime Cockpit cloud bridge",
        command: "pnpm -w longwalls:runtime-cockpit-live-mobile:v1:check",
        proof: "Cloud/mobile visibility is checked with signed snapshots, replay protection and explicit partial/blocked status if live auth remains unverified.",
        scope: "Observability",
      },
    ],
    claimBoundary:
      "Running A75 is not validated A75. Runtime visibility proves supervision, not deep app generation.",
  },
  {
    id: "m-v1-depth-unlock-next",
    horizon: "Next after A75",
    targetVersion: "v1",
    title: "Unlock real Codex depth without wasting budget",
    status: "planned",
    headline:
      "A83-A87 and Test 2 must prove that long Codex/Jarvis runs produce materially deeper applications, not expensive shallow dashboards.",
    strategicMeaning:
      "This is the next decisive test for V1. If Test 2 produces a real multi-module app with business workflows, QA and repair, the bounded V1 claim becomes far more credible.",
    proofSummary:
      "The planned packet installs adaptive capability planning, real Codex depth lanes, anti-shell product depth gate, no-loop delta supervisor and progressive test ladder.",
    evidenceRefs: [
      "docs/sprint-longwalls-real-codex-depth-lanes-v1.md",
      "docs/sprint-longwalls-real-codex-depth-lanes-v1.manifest.json",
    ],
    proofHighlights: [
      "Test 2 runs two Codex/Jarvis lanes for 4h with hard token and egress budgets.",
      "The anti-shell gate rejects compile-green but shallow outputs.",
      "The no-loop supervisor requires meaningful deltas every 20-30 minutes.",
      "Product depth becomes a gate, not an opinion.",
    ],
    remaining: [
      "Finalize A75 first.",
      "Develop A83-A87 before launching Test 2.",
      "Reject Test 2 if it is shallow, even if all technical gates are green.",
    ],
    tests: [
      {
        name: "Button-ready gate",
        command: "pnpm -w longwalls:real-codex-depth-lanes:v1:button-ready",
        proof: "A75 must be finalized and A83-A87 gates must pass before Test 2 can start.",
        scope: "Unlock",
      },
      {
        name: "Test 2 product depth",
        proof: "At least 8 modules, 6 user-facing screens, 4 role-bound workflows, local data and business QA.",
        scope: "Depth",
      },
      {
        name: "Budget discipline",
        proof: "Per lane 5M input / 1M output, packet max 10M / 2M and egress max 1536 MB.",
        scope: "Cost",
      },
    ],
    claimBoundary:
      "This is the V1 depth gate. It cannot run before A75 and cannot pass without visible product depth.",
  },
  {
    id: "m-v2-enterprise-factory-next",
    horizon: "V2 horizon",
    targetVersion: "v2",
    title: "Turn the pilot into an enterprise software factory",
    status: "planned",
    headline:
      "The next product horizon is not more demos. It is deployment modes, signed Jarvis Execution Nodes, licensed recipe packs, offline missions, support boundaries and several deep enterprise pilots.",
    strategicMeaning:
      "V2 is where LongWalls becomes sellable: customers can choose SaaS control plane, managed worker, local office server, appliance or premium full-local license without exposing the whole Jarvis brain, while also seeing credible trust and compliance readiness.",
    proofSummary:
      "A76-A82 plus Test 3 must turn the architecture into a commercial worker model, prove at least one 12h two-lane deep build, add the Trust & Compliance lane T1-T14, and add Enterprise Sales Readiness ESR1-ESR12.",
    evidenceRefs: [
      "docs/longwalls-commercial-strategy-v1.md",
      "docs/longwalls-enterprise-sales-readiness-roadmap-v1.md",
      "docs/industrial-app-factory-roadmap-to-100.md",
    ],
    proofHighlights: [
      "Commercial deployment modes and entitlement boundaries are explicit.",
      "Recipe/skill packs become productized, signed, versioned and claim-capped.",
      "Offline mission bundles keep work alive when customer internet is down.",
      "SOC2-ready, VAPT-ready, privacy, vendor risk, secure SDLC and AI governance become evidence-backed product surfaces.",
      "Legal, procurement, support, insurance, accessibility, vulnerability disclosure and board-risk reporting become part of the enterprise buyer pack.",
      "Test 3 should produce a genuinely serious enterprise app candidate.",
    ],
    remaining: [
      "Build A76-A82 after the V1 depth unlock sequence.",
      "Build T1-T12 before broad enterprise procurement claims.",
      "Build ESR1-ESR12 before serious listed-group sales motion.",
      "Keep SOC2/VAPT/ISO claims capped to readiness until external audit or test evidence exists.",
      "Run and pass Test 3 only if Test 2 reaches real depth.",
      "Validate several enterprise domains on the same lane, not custom shortcuts.",
    ],
    tests: [
      {
        name: "Commercial worker packet",
        proof: "SaaS, managed worker, local server, appliance and full-local license boundaries are enforced by entitlement and claim caps.",
        scope: "Commercial model",
      },
      {
        name: "Signed recipe packs",
        proof: "Packs are typed, signed, licensed, dependency-aware and usable by Jarvis without leaking proprietary IP unnecessarily.",
        scope: "Packs",
      },
      {
        name: "Trust and compliance lane",
        proof: "Trust taxonomy, control map, Foundry trust pack, Jarvis compiler, VAPT readiness, Trust Cockpit and SOC2 readiness are evidence-capped.",
        scope: "Compliance",
      },
      {
        name: "Enterprise sales readiness lane",
        command: "pnpm -w longwalls:enterprise-sales-readiness:v1:check",
        proof:
          "Buyer-facing legal, procurement, support, assurance, insurance, accessibility, vulnerability and board-risk readiness are separated from cosmetic sales material.",
        scope: "Commercial readiness",
      },
      {
        name: "Test 3 12h depth run",
        proof: "Two lanes, 12h, restart/repair, data persistence, broad business QA and deeper module coverage.",
        scope: "Depth",
      },
    ],
    claimBoundary:
      "V2 is not closed by a commercial document. It requires real execution-node and deep-pilot proof.",
  },
  {
    id: "m-v3-listed-grade-autonomy-next",
    horizon: "V3 frontier",
    targetVersion: "v3",
    title: "Reach listed-company-grade autonomous software production",
    status: "planned",
    headline:
      "The final horizon is a governed Jarvis-like production partner: multi-day deep builds, multiple app categories, production controls, staffed operations and parallel app supervision.",
    strategicMeaning:
      "This is the Iron Man target. The product becomes credible for PME and listed groups only when autonomy, quality, security, cost, support and governance hold together over real time.",
    proofSummary:
      "Test 4, production bridge, live enterprise controls, multi-app scheduler, observability/support/hypercare and honest claim boards must all pass.",
    evidenceRefs: [
      "docs/longwalls-v1-v3-product-vision-critical-review.md",
      "docs/sprint-longwalls-real-codex-depth-lanes-v1.md",
      "docs/longwalls-commercial-strategy-v1.md",
    ],
    proofHighlights: [
      "Multiple 48h deep Codex runs must produce real applications, not shells.",
      "ERP-like operations, finance/control, CRM/service, workflow/compliance and customer portal categories must pass.",
      "Live IAM/OIDC, WAF/SIEM/VAPT, observability, RPO/RTO, support and rollback must be verified.",
      "SOC2/VAPT/ISO claims must be externally tested, audited or explicitly limited to readiness.",
      "Dozens-parallel claims require scheduler, isolation, budgets, ledgers and failure containment.",
    ],
    remaining: [
      "Prove V1 depth first.",
      "Prove V2 execution-node and commercial worker model next.",
      "Prove Trust & Compliance operating evidence before any listed-group-grade assurance claim.",
      "Only then promote V3 claims with live production and multi-day evidence.",
    ],
    tests: [
      {
        name: "48h Codex endurance",
        proof: "Per lane 25M input / 5M output, packet max 50M / 10M, egress max 1536 MB and no unsafe external egress.",
        scope: "Autonomy",
      },
      {
        name: "Enterprise production controls",
        proof: "Live IAM/OIDC, WAF/SIEM/VAPT, observability, RPO/RTO, support, hypercare and rollback drills must pass.",
        scope: "Production",
      },
      {
        name: "Multi-app operating model",
        proof: "Parallel runs need scheduler, isolation, budgets, ledgers, failure containment and cockpit visibility.",
        scope: "Scale",
      },
    ],
    claimBoundary:
      "V3 is the target, not the current state. No V3 claim is valid without live production and multi-day evidence.",
  },
];

export function summarizeMilestones(milestones: LongWallsStrategicMilestone[] = longwallsMilestones) {
  return milestones.reduce(
    (summary, milestone) => {
      summary.total += 1;
      summary[milestone.status] += 1;
      return summary;
    },
    { total: 0, validated: 0, running: 0, planned: 0, blocked: 0 },
  );
}

export function groupMilestonesByVersion(milestones: LongWallsStrategicMilestone[] = longwallsMilestones) {
  const initialGroups: Record<LongWallsProductVersion, LongWallsStrategicMilestone[]> = {
    foundation: [],
    v1: [],
    v2: [],
    v3: [],
  };

  return milestones.reduce((groups, milestone) => {
    groups[milestone.targetVersion].push(milestone);
    return groups;
  }, initialGroups);
}
